import { useEffect, useState } from 'react';
import { FaArrowRight, FaCheckCircle, FaMobileAlt } from 'react-icons/fa';
import api from '../../services/api';
import { onboardingService } from '../../services/onboarding';
import { rastrearTeste } from '../../services/rastreamentoTeste';
import { obterIdTenent, obterNomeUsuario } from '../../services/seguranca';
import { useAppSelector } from '../../store/hooks';
import DashboardLocador, { TotaisDashboard } from './app-locador/DashboardLocador';
import { calcularEscalaCelular } from './celular-locatario/CelularLocatario';
import { ColunaRoteiro, Entrega, GradeAppLocatario, RotuloCelular } from './celular-locatario/estilosEtapa';
import QrLojas from './QrLojas';
import { Aguardando, Aviso, Botao, Credenciais, Rodape } from './styles';

const ANDROID = 'https://play.google.com/store/apps/details?id=com.locasmartpro.gestaodelocadorasapplocadores';
const IOS = 'https://apps.apple.com/br/app/locasmartpro-app-locador/id6757645086';
const INTERVALO_MS = 4000;

const TOTAIS_VAZIOS: TotaisDashboard = {
    trocasDeOleo: 0, vistorias: 0, locacoesAtivas: 0, veiculosDisponiveis: 0, emLocacao: 0,
    investido: 0, aVencer: 0, recebidos: 0, emAtraso: 0, multas: 0,
};

interface Props {
    email: string;
    aoConcluir: () => void;
}

const num = (v: any) => Number(v) || 0;

function iniciaisDe(nome: string) {
    const partes = nome.trim().split(/\s+/).filter(Boolean);
    return ((partes[0]?.[0] ?? '') + (partes.length > 1 ? partes[partes.length - 1][0] : '')).toUpperCase() || 'LS';
}

// Mesmo formato da etapa do App do Locatário: o celular à esquerda e, à direita, só o essencial
// (o que é, onde baixar e com que conta entrar). A tela confirma sozinha quando o app conecta.
export default function EtapaAppLocador({ email, aoConcluir }: Props) {
    const token = useAppSelector(state => state.token);
    const [conectado, setConectado] = useState(false);
    const [totais, setTotais] = useState<TotaisDashboard>(TOTAIS_VAZIOS);
    const [escala, setEscala] = useState(calcularEscalaCelular);

    useEffect(() => {
        if (conectado) return;
        let ativo = true;
        const verificar = () => onboardingService.appLocadorConectado()
            .then(ok => {
                if (!ativo || !ok) return;
                ativo = false;
                rastrearTeste('onboarding_app_locador_conectado');
                setConectado(true);
            })
            .catch(() => undefined);
        verificar();
        const id = window.setInterval(verificar, INTERVALO_MS);
        return () => { ativo = false; window.clearInterval(id); };
    }, [conectado]);

    useEffect(() => {
        const hoje = new Date();
        const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        api.get(`/DashboardPorEmpresa/${obterIdTenent(token)}`, {
            params: { dataInicial: iso(new Date(hoje.getFullYear(), hoje.getMonth(), 1)), dataFinal: iso(new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0)) },
        })
            .then(({ data: d }) => setTotais({
                trocasDeOleo: num(d?.totalizadoresTrocasDeOleo),
                vistorias: num(d?.totalizadoresVistorias),
                locacoesAtivas: num(d?.totalizadoresLocacoesAtivas),
                veiculosDisponiveis: num(d?.totalizadoresVeiculosDisponiveis),
                emLocacao: num(d?.totalizadoresVeiculosEmLocacao),
                investido: num(d?.investidoEmVeiculo),
                aVencer: num(d?.totalizadoresSomaPagamentosPendentes) + num(d?.totalizadoresSomaLancamentoEntradaPendente),
                recebidos: num(d?.totalizadoresSomaPagamentosPagos) + num(d?.totalizadoresSomaLancamentoEntradaResolvido),
                emAtraso: num(d?.totalizadoresSomaPagamentosAtraso) + num(d?.totalizadoresSomaLancamentoEntradaVencidas),
                multas: num(d?.totalizadoresSomaMultas),
            }))
            .catch(() => undefined);
    }, [token]);

    useEffect(() => {
        const aoRedimensionar = () => setEscala(calcularEscalaCelular());
        window.addEventListener('resize', aoRedimensionar);
        return () => window.removeEventListener('resize', aoRedimensionar);
    }, []);

    const nomeUsuario = obterNomeUsuario(token) || '';

    return (
        <>
            <GradeAppLocatario>
                <div className="celular">
                    <RotuloCelular><FaMobileAlt /> App do Locador (você)</RotuloCelular>
                    <DashboardLocador primeiroNome={nomeUsuario.split(' ')[0] || 'Locador'} iniciais={iniciaisDe(nomeUsuario)} totais={totais} escala={escala} />
                </div>

                <ColunaRoteiro>
                    <h1>Sua locadora no celular</h1>
                    <p className="sub">Avisos dos pagamentos do dia, lembretes de despesas e o resumo da locadora, sem abrir o computador.</p>

                    <Entrega>
                        <h3>Para você começar a usar</h3>
                        <div className="linha">
                            <QrLojas android={ANDROID} ios={IOS} tamanho={104} />
                            <Credenciais style={{ marginTop: 0, maxWidth: 420 }}>
                                <div className="item">
                                    <div><small>Login</small><strong>{email || 'o seu e-mail'}</strong></div>
                                </div>
                                <div className="item">
                                    <div><small>Senha</small><strong>a mesma deste acesso</strong></div>
                                </div>
                            </Credenciais>
                        </div>
                        <div style={{ marginTop: 12 }}>
                            {conectado
                                ? <Aviso $tipo="ok"><FaCheckCircle /> Celular conectado. Confira a notificação que acabamos de enviar.</Aviso>
                                : <Aguardando>Aguardando o seu primeiro acesso pelo app...</Aguardando>}
                        </div>
                    </Entrega>
                </ColunaRoteiro>
            </GradeAppLocatario>

            <Rodape style={{ marginTop: 14, paddingTop: 14 }}>
                <span>{conectado ? '' : 'Pode baixar depois, se preferir.'}</span>
                <Botao type="button" onClick={aoConcluir}>Continuar <FaArrowRight /></Botao>
            </Rodape>
        </>
    );
}
